import * as Sentry from '@sentry/react';

/**
 * Noise we never want to count as errors:
 *
 *  - Stale-chunk-after-deploy (CPMS-WEB-6 / CPMS-WEB-4): a lazy import 404s
 *    because a new build replaced its hashed chunk. This is self-healing — the
 *    `vite:preloadError` handler in main.tsx + the ErrorBoundary reload the page.
 *    Capturing it just re-opens a "regressed" issue on every deploy.
 *  - Deliberate request cancellation (CPMS-WEB-M): the upload hook aborts its
 *    in-flight batch on unmount by design. An aborted XHR is not a failure.
 *
 * These run as a global backstop; the source sites (ErrorBoundary, useUpload)
 * also skip capture, but this catches any variant they miss.
 */
const STALE_CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /is not a valid JavaScript MIME type/i,
];

function isStaleChunkMessage(msg: string): boolean {
  return STALE_CHUNK_PATTERNS.some((re) => re.test(msg));
}

function isCanceledRequest(err: unknown): boolean {
  const e = err as { name?: string; code?: string } | undefined;
  return e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED';
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enabled: import.meta.env.PROD,
  // Cheap string-match drop for the common stale-chunk variants.
  ignoreErrors: STALE_CHUNK_PATTERNS,
  beforeSend(event, hint) {
    const err = hint?.originalException;
    if (isCanceledRequest(err)) return null;
    const message =
      (err as Error | undefined)?.message ?? event.exception?.values?.[0]?.value ?? '';
    if (isStaleChunkMessage(message)) return null;
    return event;
  },
});
